import React from "react";
import {
    useTheme,
    Card,
    CardProps,
    BoxProps,
    TableContainerProps,
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import * as companyService from "services/company-service";
import * as userService from "services/identityUser-service";
import * as Styles from "pages/development/settings/tabs/companies/listCompanies.styles";
import { useTranslation } from "react-i18next";
import DataTable from "components/formsUI/DataTable";
import { GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditModal from "./modals/editResponsibleModal";
import CreateModal from "./modals/createResponsibleModal";
import { Message, MessageRef } from "components/messages/message";
import { IPropsSettings } from "../../Settings";

export default function ListResponsible(props: IPropsSettings) {
    const { tenantId } = props;
    const childRef = React.useRef<MessageRef>();
    const theme = useTheme();
    const { t } = useTranslation();
    const [showErrorMessage, setShowErrorMessage] =
        React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [companies, setCompanies] = React.useState<ResponsiblePaged[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<{
        open: boolean;
        responsible: ResponsiblePaged | null;
    }>({ open: false, responsible: null });
    const [createOpen, setCreateOpen] = React.useState<boolean>(false);

    const [rowCount, setRowCount] = React.useState<number>(0);
    const [page, setPage] = React.useState<number>(0);
    const [pageSize, setPageSize] = React.useState<number>(25);
    const [direction] = React.useState<string>("");
    const [sortOrder] = React.useState<string>("");

    const [companyName, setCompanyName] = React.useState<string>("");
    const [responsible, setResponsible] = React.useState<string>("");
    const [responsibleFirstName, setResponsibleFirstName] =
        React.useState<string>("");
    const [responsibleName, setResponsibleName] = React.useState<string>("");

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        pageSize,
        direction,
        sortOrder,
        companyName,
        responsible,
        responsibleFirstName,
        responsibleName,
        tenantId,
    ]);

    const fetchData = (): void => {
        setIsLoading(true);
        setShowErrorMessage(false);
        companyService
            .getResponsibles(
                page + 1,
                pageSize,
                direction,
                sortOrder,
                companyName,
                responsible,
                responsibleFirstName,
                responsibleName,
                tenantId,
            )
            .then((res) => {
                console.log(res);
                setCompanies(res.results);
                setRowCount(res.rowCount);
                setIsLoading(false);
            })
            .catch((err) => {
                setErrorMessage(err.message);
                setShowErrorMessage(true);
                setIsLoading(false);
            });
    };

    const handleOpenCreate = () => {
        setCreateOpen(!createOpen);
    };

    const handleSendLogin = (userId: string) => {
        userService.sendLogin(userId, tenantId).then((res) => {
            // showMessage(t('responsible.login'), "success", 10000);
            childRef.current?.showMessage(
                t("settings.responsibles.login"),
                "success",
                10000,
            );
        });
    };

    const listCompaniesWrapperProps: CardProps = {
        sx: Styles.listRequestsWrapper(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const tableContainerProps: TableContainerProps = {
        sx: Styles.tableContainer(theme),
        // sx: {
        //     // minHeight: "400px",
        //     overflow: "auto",
        //     maxHeight: `100%`,
        //     height: `100%`,
        // },
    };

    return (
        <Box>
            <Message ref={childRef} />
            <Typography variant="h4">
                {t("settings.responsibles.title")}
            </Typography>
            <Box>{t("settings.responsibles.description")}</Box>

            <Card {...listCompaniesWrapperProps}>
                <Box {...titleWrapperProps}>
                    {showErrorMessage && (
                        <Box color="error.main">
                            <Box>{errorMessage}</Box>
                            <Button
                                sx={{ marginTop: "5px" }}
                                color="error"
                                variant="outlined"
                                onClick={() => {
                                    fetchData();
                                }}
                            >
                                {t("requests.errorButton")}
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box {...tableContainerProps}>
                    <DataTable
                        columns={[
                            {
                                field: "name",
                                headerName: t(
                                    "settings.responsibles.labels.responsibleName",
                                ),
                                renderHeader: () => (
                                    <TextField
                                        label={t(
                                            "requestIndividual.step2.responsibleLastName",
                                        )}
                                        value={responsibleName}
                                        onChange={(e) => {
                                            setResponsibleName(e.target.value);
                                        }}
                                        size="small"
                                        sx={{
                                            backgroundColor: "white",
                                            marginTop: "0.50rem",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                            },
                            {
                                field: "firstName",
                                headerName: t(
                                    "requestIndividual.step2.responsibleFirstName",
                                ),
                                renderHeader: () => (
                                    <TextField
                                        label={t(
                                            "requestIndividual.step2.responsibleFirstName",
                                        )}
                                        value={responsibleFirstName}
                                        onChange={(e) => {
                                            setResponsibleFirstName(
                                                e.target.value,
                                            );
                                        }}
                                        size="small"
                                        sx={{
                                            backgroundColor: "white",
                                            marginTop: "0.50rem",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                            },
                            {
                                field: "email",
                                headerName: t(
                                    "settings.responsibles.labels.responsible",
                                ),
                                renderHeader: () => (
                                    <TextField
                                        label={t(
                                            "requestIndividual.step2.responsibleEmail",
                                        )}
                                        value={responsible}
                                        onChange={(e) => {
                                            setResponsible(e.target.value);
                                        }}
                                        size="small"
                                        sx={{
                                            backgroundColor: "white",
                                            marginTop: "0.50rem",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                            },
                            {
                                field: "companies",
                                headerName: t(
                                    "settings.responsibles.labels.name",
                                ),
                                renderHeader: () => (
                                    <TextField
                                        label={t(
                                            "settings.responsibles.labels.name",
                                        )}
                                        value={companyName}
                                        onChange={(e) => {
                                            setCompanyName(e.target.value);
                                        }}
                                        size="small"
                                        sx={{
                                            marginTop: "0.50rem",
                                            backgroundColor: "white",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                                renderCell: (params) => (
                                    <Box
                                        sx={{
                                            paddingTop: "0.50rem",
                                            paddingBottom: "0.50rem",
                                        }}
                                    >
                                        {params.value.map((company: any) => (
                                            <Box
                                                key={company.id}
                                                sx={{ textAlign: "center" }}
                                            >
                                                {company.name}
                                            </Box>
                                        ))}
                                    </Box>
                                ),
                            },
                            {
                                field: "edit",
                                hideable: false,
                                renderHeader: () => <Box></Box>,
                                renderCell: (
                                    params: GridRenderCellParams<ResponsiblePaged>,
                                ) => (
                                    <Box>
                                        <Tooltip
                                            placement="top"
                                            arrow
                                            title={t(
                                                "settings.responsibles.editTooltip",
                                            )}
                                        >
                                            <IconButton
                                                onClick={() => {
                                                    setOpen({
                                                        open: true,
                                                        responsible: params.row,
                                                    });
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                            placement="top"
                                            arrow
                                            title={t(
                                                "settings.responsibles.sendLogin",
                                            )}
                                        >
                                            <IconButton
                                                onClick={() => {
                                                    handleSendLogin(
                                                        params.row.id as string,
                                                    );
                                                }}
                                            >
                                                <ManageAccountsIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ),
                            },
                        ]}
                        rows={companies}
                        rowCount={rowCount}
                        paginationModel={{ pageSize: pageSize, page: page }}
                        updatePagination={(params: GridPaginationModel) => {
                            setPageSize(params.pageSize);
                            setPage(params.page);
                        }}
                        loading={isLoading}
                        columnVisibilityModel={{}}
                        onColumnVisibilityModelChange={() => {}}
                        initialColumnVisibility={{}}
                        toolbar={[
                            <Button
                                variant="contained"
                                onClick={handleOpenCreate}
                                sx={{ marginTop: "1rem" }}
                            >
                                {t("settings.responsibles.newButton")}
                            </Button>,
                        ]}
                        reset={() => {}}
                        showToolbar={false}
                        autoHeight={["companies"]}
                        minHeight="500px"
                    />
                </Box>
                <EditModal
                    tenantId={tenantId}
                    open={open.open}
                    responsible={open.responsible}
                    handleClose={() =>
                        setOpen({ open: false, responsible: null })
                    }
                    refresh={fetchData}
                />
                <CreateModal
                    tenantId={tenantId}
                    open={createOpen}
                    onClose={handleOpenCreate}
                    refresh={fetchData}
                />
            </Card>
        </Box>
    );
}
